import { ApiError, useAuthenticatedQuery } from "../lib/api";
import type { PerformancePeriod } from "../lib/date";

/** What `PerformancePeriodSelector` hands its parent: the period id and dates. */
export type PerformanceSelection = {
  period: PerformancePeriod;
  /** `YYYY-MM-DD`; only read for a custom period. */
  startDate?: string;
  endDate?: string;
};

/**
 * The Performance figure (APP-2005), as `GET /performance-drivers/{households,
 * accounts}/:id/figure` answers. `return` is a fraction (0.042 is 4.2%) or
 * null when BridgeFT has no return for the selection and period.
 */
export type PerformanceFigure = {
  asOfDate: string | null;
  period: {
    id: string;
    start: string | null;
    end: string | null;
    available: boolean;
  };
  return: number | null;
  chained?: unknown;
  excludedAccounts: unknown[];
};

export function performanceFigurePath(
  kind: "households" | "accounts",
  id: number,
  selection: PerformanceSelection,
  accountIds?: number[],
) {
  const params = new URLSearchParams({ period: selection.period });
  if (selection.period === "custom") {
    if (selection.startDate) params.set("startDate", selection.startDate);
    if (selection.endDate) params.set("endDate", selection.endDate);
  }
  if (kind === "households" && accountIds?.length) {
    params.set("accountIds", accountIds.join(","));
  }
  return `/performance-drivers/${kind}/${id}/figure?${params.toString()}`;
}

/**
 * The query string for the rows read and the chart read: the dates of the
 * figure response, so the whole panel shows the Performance Drivers period
 * (R83). Without them (no figure yet, or the period has no dates) it is the
 * selector's own dates.
 */
export function figurePeriodParams(
  figure: PerformanceFigure | undefined,
  selectorParams: string,
) {
  const { start, end } = figure?.period ?? {};
  return start && end ? `&start-date=${start}&end-date=${end}` : selectorParams;
}

/** Whether the figure is a chained return (R4). */
export const isChained = (figure: PerformanceFigure | undefined) =>
  Boolean(figure?.chained);

export function useQueryPerformanceFigure(
  kind: "households" | "accounts",
  id: number | undefined,
  selection: PerformanceSelection | undefined,
  accountIds?: number[],
) {
  const path =
    id !== undefined && selection
      ? performanceFigurePath(kind, id, selection, accountIds)
      : "";
  const { data, isPending, isError } = useAuthenticatedQuery<
    { data: PerformanceFigure },
    { data: PerformanceFigure }
  >({
    queryKey: [path],
    urlBuilderFn: () => path,
    queryFn: async (response) =>
      response as unknown as { data: PerformanceFigure },
    queryOptions: {
      enabled: path !== "",
      retry: (failures, err) =>
        !(err instanceof ApiError && err.status >= 400 && err.status < 500) &&
        failures < 3,
    },
  });
  return { figure: data?.data, isPending, isError };
}
